import { Chunk, Exit, HashSet, pipe, Schema, SchemaGetter } from 'effect';
// `pipe` and `Chunk` are still used below by `warehouseFilterStateSetToUrlArray`.

import type { WarehouseFilterStateSet } from '../state/warehouse';
import {
  WarehouseCode as WarehouseCodeSchema,
  WarehouseFilterStateSet as WarehouseFilterStateSetSchema,
} from '../state/warehouse';

const warehouseFilterStateSetToUrlArray = (warehouseSet: WarehouseFilterStateSet): string[] =>
  pipe(warehouseSet, Chunk.fromIterable, Chunk.toArray);

// v4: `Chunk.filterMap` now expects `(input, i) => Result<B, X>` (Result-shaped,
// not Option-shaped). Decode each candidate and keep only the Success values.
export const urlArrayToWarehouseFilterStateSet = (
  warehouseArray: readonly string[]
): WarehouseFilterStateSet => {
  const decoded: WarehouseCodeSchema[] = [];
  for (const s of warehouseArray) {
    const exit = Schema.decodeUnknownExit(WarehouseCodeSchema)(s);
    if (Exit.isSuccess(exit)) decoded.push(exit.value);
  }
  return HashSet.fromIterable(decoded);
};

// v4: `Schema.decodeTo(target, transformation)` takes `{decode, encode}` where
// each is a `Getter`, not the v3 `SchemaTransformation.transform(...)` wrapper.
export const WarehouseSerializable = Schema.String.pipe(
  Schema.decodeTo(Schema.toType(WarehouseFilterStateSetSchema), {
    decode: SchemaGetter.transform((str: string) =>
      urlArrayToWarehouseFilterStateSet(str.split(',').filter((s) => s.length > 0))
    ),
    encode: SchemaGetter.transform((warehouseSet: WarehouseFilterStateSet) =>
      warehouseFilterStateSetToUrlArray(warehouseSet).join(',')
    ),
  })
);

export const WarehouseUrlQuery = Schema.Struct({
  warehouseStock: Schema.optional(Schema.Array(Schema.String)),
});

export const WarehouseToUrl = Schema.toType(WarehouseFilterStateSetSchema).pipe(
  Schema.decodeTo(WarehouseUrlQuery, {
    decode: SchemaGetter.transform((warehouseSet: WarehouseFilterStateSet) => {
      const warehouseArray = warehouseFilterStateSetToUrlArray(warehouseSet);
      return warehouseArray.length > 0 ? { warehouseStock: warehouseArray } : {};
    }),
    encode: SchemaGetter.transform(
      (urlData: { readonly warehouseStock?: readonly string[] | undefined }) =>
        urlArrayToWarehouseFilterStateSet(urlData.warehouseStock ?? [])
    ),
  })
);
